import { getTranslation } from '../localizations/translations';
import { BackspaceIcon, EnterIcon, SpaceIcon } from './Icons';

export interface KeyboardKey {
  label?: {
    fi: string;
    sv: string;
  };
  ariaLabel?: { fi: string; sv: string };
  icon?: () => JSX.Element;
  width?: number;
  value: string;
}

const softKeyboardBackspaceKey: KeyboardKey = {
  icon: BackspaceIcon,
  value: '<backspace>',
  ariaLabel: {
    fi: getTranslation('keyboard_backspace', 'fi'),
    sv: getTranslation('keyboard_backspace', 'sv'),
  },
  width: 2,
};

const softKeyboardSpaceKey: KeyboardKey = {
  label: {
    fi: getTranslation('keyboard_space', 'fi'),
    sv: getTranslation('keyboard_space', 'sv'),
  },
  value: '<space>',
  width: 3,
};

const softKeyboardSearchSpaceKey: KeyboardKey = {
  ariaLabel: {
    fi: getTranslation('keyboard_space', 'fi'),
    sv: getTranslation('keyboard_space', 'sv'),
  },
  value: '<space>',
  icon: SpaceIcon,
  width: 2,
};

const softKeyboardClearKey: KeyboardKey = {
  label: {
    fi: getTranslation('keyboard_clear', 'fi'),
    sv: getTranslation('keyboard_clear', 'sv'),
  },
  value: '<clear>',
  width: 3,
};

const softKeyboardSearchClearKey: KeyboardKey = {
  label: {
    fi: getTranslation('keyboard_clear', 'fi'),
    sv: getTranslation('keyboard_clear', 'sv'),
  },
  value: '<clear>',
  width: 6,
};

const softKeyboardSymbolsKey: KeyboardKey = {
  ariaLabel: {
    fi: getTranslation('keyboard_symbols', 'fi'),
    sv: getTranslation('keyboard_symbols', 'sv'),
  },
  label: {
    fi: '...',
    sv: '...',
  },
  value: '<symbols>',
};

const softKeyboardSearchSymbolsKey: KeyboardKey = {
  ariaLabel: {
    fi: getTranslation('keyboard_symbols', 'fi'),
    sv: getTranslation('keyboard_symbols', 'sv'),
  },
  label: {
    fi: '&123',
    sv: '&123',
  },
  value: '<symbols>',
  width: 2,
};

const softKeyboardCapsKey: KeyboardKey = {
  ariaLabel: {
    fi: getTranslation('keyboard_caps', 'fi'),
    sv: getTranslation('keyboard_caps', 'sv'),
  },
  label: {
    fi: 'A/a',
    sv: 'A/a',
  },
  value: '<caps>',
};

const softKeyboardAlphabetKey: KeyboardKey = {
  ariaLabel: {
    fi: getTranslation('keyboard_alphabet', 'fi'),
    sv: getTranslation('keyboard_alphabet', 'sv'),
  },
  label: {
    fi: 'abc',
    sv: 'abc',
  },
  value: '<alphabet>',
};

const softKeyboardSearchAlphabetKey: KeyboardKey = {
  ariaLabel: {
    fi: getTranslation('keyboard_alphabet', 'fi'),
    sv: getTranslation('keyboard_alphabet', 'sv'),
  },
  label: {
    fi: 'abc',
    sv: 'abc',
  },
  value: '<alphabet>',
  width: 2,
};

const softKeyboardEnterKey: KeyboardKey = {
  icon: EnterIcon,
  value: '<enter>',
  ariaLabel: {
    fi: getTranslation('keyboard_enter', 'fi'),
    sv: getTranslation('keyboard_enter', 'sv'),
  },
  width: 2,
};

const softKeyboardEmptyKey: KeyboardKey = {
  label: {
    fi: ' ',
    sv: ' ',
  },
  value: '<empty>',
};

export const topRow = [
  softKeyboardSpaceKey,
  softKeyboardBackspaceKey,
  softKeyboardClearKey,
];

const atoz: KeyboardKey[] = [...Array(26)].map(
  (_, index: number): KeyboardKey => {
    return {
      value: String.fromCharCode(index + 97),
    };
  }
);

const smallUmlauts: KeyboardKey[] = [
  { value: 'å' },
  { value: 'ä' },
  { value: 'ö' },
];

const numbers: KeyboardKey[] = [...Array(10)].map((_, index): KeyboardKey => {
  return {
    value: `${index}`,
  };
});

export const bottomRow: KeyboardKey[] = [
  { value: '.' },
  { value: '-' },
  softKeyboardSymbolsKey,
  softKeyboardCapsKey,
  { value: '.com', width: 2 },
  softKeyboardEnterKey,
];

const symbolChars = [
  '@',
  '&',
  '-',
  '.',
  ',',
  ':',
  '"',
  "'",
  '`',
  '?',
  '!',
  '/',
  '#',
  '*',
  '€',
  '$',
  '¢',
  '+',
  '=',
  '(',
  ')',
  '[',
  ']',
  '_',
  '%',
  '|',
  '§',
  '<',
  '>',
  ';',
  '{',
  '}',
];

const searchSymbolChars = [
  '(',
  ')',
  '@',
  '&',
  '-',
  '+',
  ':',
  ';',
  '/',
  '€',
  '"',
  "'",
  '!',
  '?',
  '.',
  ',',
  '*',
  '~',
  '%',
  '#',
];

export const symbols: KeyboardKey[] = symbolChars.map(char => {
  if (char === '/') {
    return {
      value: '<slash>',
      label: {
        fi: '/',
        sv: '/',
      },
    };
  } else {
    return {
      value: char,
    };
  }
});

export const searchSymbols: KeyboardKey[] = searchSymbolChars.map(char => {
  if (char === '/') {
    return {
      value: '<slash>',
      label: {
        fi: '/',
        sv: '/',
      },
    };
  } else {
    return {
      value: char,
    };
  }
});

const emptyChars: KeyboardKey[] = [
  softKeyboardEmptyKey,
  softKeyboardEmptyKey,
  softKeyboardEmptyKey,
  softKeyboardEmptyKey,
  softKeyboardEmptyKey,
];

export const alphaNumericalSection = atoz
  .concat(smallUmlauts)
  .concat(numbers)
  .concat({ value: '@' });

export const specialKeysBottomRow = emptyChars
  .concat(softKeyboardAlphabetKey)
  .concat(softKeyboardEnterKey);

export const keys: [KeyboardKey[][], KeyboardKey[][]] = [
  [topRow, alphaNumericalSection, bottomRow],
  [topRow, symbols, specialKeysBottomRow],
];

export enum KeyboardPageIndex {
  SMALL_LETTERS,
  SYMBOLS,
}

export const searchAlphabetSection = atoz
  .concat(smallUmlauts)
  .concat({ value: '-' });

export const searchSymbolsSection = numbers.concat(searchSymbols);

export const searchBottomRow = [
  softKeyboardSearchSymbolsKey,
  softKeyboardSearchSpaceKey,
  softKeyboardBackspaceKey,
];

const searchSymbolsBottomRow = [
  softKeyboardSearchAlphabetKey,
  softKeyboardSearchSpaceKey,
  softKeyboardBackspaceKey,
];

export const searchClearRow = [softKeyboardSearchClearKey];

export const searchSymbolKeys = [
  numbers,
  searchSymbols,
  searchSymbolsBottomRow,
  searchClearRow,
];

export const searchKeys: [KeyboardKey[][], KeyboardKey[][]] = [
  [searchAlphabetSection, searchBottomRow, searchClearRow],
  [searchSymbolsSection, searchSymbolsBottomRow, searchClearRow],
];

/**
 * Util for navigation in SquareKeyboards, where the width of elements is taken into consideration,
 * eg. with an example layout of
 *
 * [Space][Remove] where buttons are equal width, and the grid below is 6 characters wide, we end up with
 *
 * [0, 0, 0, 1, 1, 1]
 *
 * The focus.offset when entering a row like this can then be used directly to determine which key to focus
 */
export const spreadOffsets = (row: KeyboardKey[]) =>
  row.map((elem, index) => Array<number>(elem.width || 1).fill(index)).flat();
